<script setup lang="ts">
import { useFlowControlStore } from '@components/flow-controller'
import { IconExclamationCircle } from '@tabler/icons-vue'
import { computed, ref } from 'vue'

import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import type { Schema } from '@tix/openapi-sdk'

import { pluralize } from '@/helpers/StringHelpers'
import { getAssetUrl } from '@/helpers/assets'
import { getErrorForAction } from '@/helpers/flowActionErrors'
import { getPickNOptionDisplayName } from '@/helpers/flowOptionLabel'
import { formatPickNSelectionValue } from '@/helpers/flowStepValue'

import { useBundleStepTracking } from '@/composables/useBundleStepTracking'

import ActionHeader from './ActionHeader.vue'
import SelectableCard from './SelectableCard.vue'
import SelectionStatusBar from './SelectionStatusBar.vue'
import type { ActionValidationError, SelectableItemBase } from './types'

const actionType: Schema<'action_pick_n'>['type'] = 'pick_n'

// #TODO - these types are TEMPORARY because the api is not yet updated to match the fields the designs suggest we need
// https://ticketure.slack.com/archives/C0A0F0CDLT1/p1765145861906239
type OriginalPickNActionPrompt = Schema<'action_prompt_pick_n'>
type OriginalOption = NonNullable<NonNullable<OriginalPickNActionPrompt['options']>['options']>[number]
export type ExtendedPickNActionPrompt = Omit<OriginalPickNActionPrompt, 'meta' | 'options'> & {
  meta: {
    summary_title: string
    prompt?: string
  }
  options: Omit<OriginalPickNActionPrompt['options'], 'options'> & {
    options: (OriginalOption & {
      // #TODO these dont exist in the api
      dates?: string
      price?: string
      meta?: {
        card_title?: string
        description?: string
        image?: {
          asset_id: string
          alt_text?: string
          no_crop: boolean
        }
      }
    })[]
  }
}

type OptionItem = SelectableItemBase & {
  price?: string
  dates?: string
  imageUrl?: string
  imageAlt?: string
  description?: string
}

const store = useFlowControlStore()
const { trackBundleStep } = useBundleStepTracking()

const action = computed(
  () => (store.action?.type === actionType && (store.action as ExtendedPickNActionPrompt)) || null,
)

// Register input getter with the store
store.registerInputGetter(() => {
  const numSelected = selectedIds.value.size
  const isValid = numSelected >= min.value && numSelected <= max.value

  if (!isValid) {
    const message =
      max.value === 1
        ? `Please select an item`
        : max.value === 0 || max.value === Infinity
          ? `Please select at least ${min.value} ${pluralize(min.value, 'item', 'items')}`
          : `Please select between ${min.value} and ${max.value} items`

    validationError.value = { type: 'client-side', message }
    return { isValid }
  }

  // reset errors on every submission
  validationError.value = null

  return { isValid, input: { selected_resolver_ids: Array.from(selectedIds.value) } }
})

// Register error handler for errors this action can handle
store.registerErrorHandler((apiError: unknown) => {
  const { message } = getErrorForAction(apiError, actionType)
  if (message) validationError.value = { type: 'server-side', message }
  return Boolean(message)
})

// Initialize from previous input if available (e.g. when navigating back)
const prevInput = store.currentActionPreviousInput
const ids = (prevInput?.type === actionType && prevInput.input?.selected_resolver_ids) || []
const selectedIds = ref<Set<string>>(new Set(ids))
// We only show validation errors after the user has attempted to submit AND the user hasn't interacted with the form since
const validationError = ref<ActionValidationError | null>(null)

// openapi-typescript index signatures widen options values to unknown
const max = computed(() => {
  const value = action.value?.options.max_n
  return typeof value === 'number' ? value : 1
})
const min = computed(() => {
  const value = action.value?.options.min_n
  return typeof value === 'number' ? value : 1
})
const isMultiple = computed(() => max.value > 1)
// TODO Should we even support required pick-ones or prevent it in the CMS as it doesn't really make sense?
// A required pick-one is a radio and cannot be cleared; anything else toggles off like a checkbox.
const canDeselect = computed(() => isMultiple.value || min.value === 0)
const selectedCount = computed(() => selectedIds.value.size)

/** Can be html */
const title = computed(() => action.value?.meta.summary_title)
/** Can be html */
const prompt = computed(() => action.value?.meta.prompt)

const items = computed<OptionItem[]>(
  () =>
    action.value?.options.options?.map((option) => {
      return {
        id: option.resolver_id,
        title: getPickNOptionDisplayName(option),
        description: option.meta?.description,
        imageUrl: option.meta?.image?.asset_id ? getAssetUrl(option.meta.image.asset_id) : undefined,
        imageAlt: option.meta?.image?.alt_text,
        price: option.price,
        dates: option.dates,
      }
    }) ?? [],
)

function handleSelect(id: string) {
  validationError.value = null

  const isSelecting = !selectedIds.value.has(id)
  if (!isSelecting && !canDeselect.value) return // re-click clears error but isn't a tracked change

  // Single mode holds at most one option, so a click replaces the set rather than adding to it
  const next = isMultiple.value ? new Set(selectedIds.value) : new Set<string>()
  if (isSelecting) next.add(id)
  else next.delete(id)
  selectedIds.value = next

  trackBundleStep(formatPickNSelectionValue(id, action.value?.options.options, isSelecting))
}

function isSelected(id: string) {
  return selectedIds.value.has(id)
}

// TODO - Improve wording of user-facing error messaging - https://ticketure-team.atlassian.net/browse/TIC-5452
const errorMessage = computed<string | null>(() => {
  if (!action.value) return `Action is not a ${actionType} action`
  if (validationError.value) return validationError.value.message ?? 'Validation error'
  return null
})
</script>

<template>
  <section class="pick-n-action">
    <template v-if="action">
      <fieldset class="fieldset">
        <legend v-shtml="prompt" class="visually-hidden" />

        <ActionHeader :description="prompt" :title="title" />

        <SelectionStatusBar
          v-if="isMultiple"
          :isError="validationError?.type === 'client-side'"
          :max="max"
          :min="min"
          :selectedCount="selectedCount"
        />

        <TransitionGroup appear class="items" name="transition-group-item" tag="div">
          <SelectableCard
            v-for="(item, index) in items"
            :id="item.id"
            :key="item.id"
            class="item"
            :dates="item.dates"
            :description="item.description"
            :imageAlt="item.imageAlt"
            :imageUrl="item.imageUrl"
            :isSelected="isSelected(item.id)"
            :price="item.price"
            :role="canDeselect ? 'checkbox' : 'radio'"
            :style="{ '--index': index }"
            :title="item.title"
            :togglable="isMultiple"
            @click="handleSelect"
          />
        </TransitionGroup>
      </fieldset>
    </template>

    <StatusMessage :icon="IconExclamationCircle" :message="errorMessage" variant="error" />
  </section>
</template>

<style scoped>
.pick-n-action {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  .fieldset {
    display: contents;
  }

  .items {
    --_min-item-width: 310px;

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--_min-item-width), 1fr));
    gap: var(--space-1);

    .item {
      min-width: var(--_min-item-width);
      flex: 0 0 var(--_min-item-width);
    }
  }

  .transition-group-item-enter-active {
    transition:
      opacity 0.4s ease,
      transform 0.4s ease;
    transition-delay: calc(180ms * (var(--index, 0) / (var(--index, 0) + 2)));
  }
  .transition-group-item-enter-from {
    opacity: 0;
    transform: translateY(30px);
  }
}
</style>
